type SentryFrame = {
  abs_path?: unknown
  filename?: unknown
}

type SentryExceptionValue = {
  stacktrace?: {
    frames?: SentryFrame[]
  }
  value?: unknown
}

type SentryBreadcrumb = {
  category?: unknown
  data?: Record<string, unknown>
}

type SentryEvent = {
  breadcrumbs?: SentryBreadcrumb[]
  exception?: {
    values?: SentryExceptionValue[]
  }
  message?: unknown
  request?: {
    url?: unknown
  }
}

type SentryHint = {
  originalException?: unknown
}

type SentryFilterOptions = {
  apiOrigin?: unknown
}

type SentryIgnoredError = {
  cause?: unknown
  ignoreSentry?: unknown
  status?: unknown
  statusCode?: unknown
}

const moduleScriptFailedPattern = /^Importing a module script failed\.?$/i
const dynamicImportFailedPattern =
  /^Failed to fetch dynamically imported module: https?:\/\/\S+\/_nuxt\/\S+\.js(?:\?\S*)?$/i
const loadFailedPattern = /^Load failed(?: \([^)]+\))?$/i
const browserApiTransportFailurePattern = /^(?:TypeError:\s*)*(?:Failed to fetch|Load failed(?: \([^)]+\))?)$/i
const nuxtChunkPattern = /\/_nuxt\/\S+\.js/i
const scriptFilePattern = /\.[cm]?js(?:[?#:]|$)/i

export function shouldDropSentryEvent(
  event: SentryEvent,
  hint?: SentryHint,
  options: SentryFilterOptions = {}
): boolean {
  if (hasSentryIgnoredMarker(hint?.originalException)) {
    return true
  }

  if (hasExpectedNuxtStatus(hint?.originalException)) {
    return true
  }

  if (hasOnlyInjectedScriptFrames(event)) {
    return true
  }

  const messages = getMessages(event, hint)

  if (messages.some((message) => moduleScriptFailedPattern.test(message) || dynamicImportFailedPattern.test(message))) {
    return true
  }

  if (
    messages.some((message) => browserApiTransportFailurePattern.test(message)) &&
    hasApiOriginSignal(event, options.apiOrigin)
  ) {
    return true
  }

  return messages.some((message) => loadFailedPattern.test(message)) && hasNuxtChunkSignal(event, hint)
}

/**
 * True when the event has stack-frame paths but none of them reference a
 * script file (.js/.mjs/.cjs) — the signature of code the browser injected
 * into the page (e.g. Chrome on iOS's __gCrWeb helpers, whose frames cite the
 * document URL). None of our bundles appear anywhere in the stack, so the
 * error cannot be ours to fix or reproduce.
 */
function hasOnlyInjectedScriptFrames(event: SentryEvent): boolean {
  const paths = (event.exception?.values ?? [])
    .flatMap((value) => value.stacktrace?.frames ?? [])
    .flatMap((frame) => [frame.filename, frame.abs_path])
    .filter((path): path is string => typeof path === 'string')

  if (paths.length === 0) {
    return false
  }

  return paths.every((path) => !scriptFilePattern.test(path))
}

function hasSentryIgnoredMarker(value: unknown, seen = new Set<unknown>()): boolean {
  if (!value || typeof value !== 'object' || seen.has(value)) {
    return false
  }

  seen.add(value)

  const error = value as SentryIgnoredError

  return error.ignoreSentry === true || hasSentryIgnoredMarker(error.cause, seen)
}

function hasExpectedNuxtStatus(value: unknown, seen = new Set<unknown>()): boolean {
  if (!value || typeof value !== 'object' || seen.has(value)) {
    return false
  }

  seen.add(value)

  const error = value as SentryIgnoredError
  const status = error.statusCode ?? error.status

  return (
    (typeof status === 'number' && Number.isInteger(status) && status >= 200 && status < 500) ||
    hasExpectedNuxtStatus(error.cause, seen)
  )
}

function getMessages(event: SentryEvent, hint?: SentryHint): string[] {
  const messages = [event.message, ...(event.exception?.values ?? []).map((value) => value.value)]
  const original = hint?.originalException

  if (original instanceof Error) {
    messages.push(original.message)
  } else if (typeof original === 'string') {
    messages.push(original)
  }

  if (original && typeof original === 'object' && 'rawMessage' in original) {
    messages.push(original.rawMessage)
  }

  return messages.filter((message): message is string => typeof message === 'string')
}

function hasNuxtChunkSignal(event: SentryEvent, hint?: SentryHint): boolean {
  const framePaths = (event.exception?.values ?? [])
    .flatMap((value) => value.stacktrace?.frames ?? [])
    .flatMap((frame) => [frame.filename, frame.abs_path])
  const originalStack = hint?.originalException instanceof Error ? hint.originalException.stack : undefined

  return [...getMessages(event, hint), ...framePaths, originalStack].some(
    (value) => typeof value === 'string' && nuxtChunkPattern.test(value)
  )
}

function hasApiOriginSignal(event: SentryEvent, apiOrigin: unknown): boolean {
  const expectedOrigin = getOrigin(apiOrigin)

  if (!expectedOrigin) {
    return false
  }

  const origins = [event.request?.url, ...(event.breadcrumbs ?? []).map((breadcrumb) => breadcrumb.data?.url)]
    .map((url) => getOrigin(url))
    .filter((origin): origin is string => typeof origin === 'string')

  return origins[origins.length - 1] === expectedOrigin
}

function getOrigin(url: unknown): string | undefined {
  if (typeof url !== 'string') {
    return undefined
  }

  try {
    return new URL(url).origin
  } catch {
    return undefined
  }
}
